import { X, UserX, Database, AlertTriangle, MapPin } from "lucide-react";
import SectionCard from "./SectionCard";
import BarChart from "./charts/BarChart";
import { formatNum } from "@/lib/constants";
import LeyendaEstado from "./pipeline/LeyendaEstado";

export default function NoShowDrawer({ data, periodo, onClose }) {
  if (!data) return null;

  const hasCentros = data.por_centro && data.por_centro.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="noshow-drawer">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-lg bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="sticky top-0 z-10 p-5 border-b border-slate-200 bg-white/95 backdrop-blur">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-500 flex items-center justify-center text-white">
                <UserX className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Detalle No-Show</h3>
                <p className="text-xs text-slate-500">Consultas presenciales · {periodo}</p>
              </div>
            </div>
            <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition"
                    aria-label="Cerrar panel">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
          <div className="mt-2">
            <LeyendaEstado estado={data.origen} />
          </div>
        </div>

        <div className="p-5 space-y-6">
          {/* KPIs principales — alineados con Grafana equipo de acceso */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total reservas</div>
              <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.total_reservas)}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Anulados</div>
              <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.anulados)}</div>
              <div className="text-xs text-rose-500 tabular-nums">{data.pct_anulacion}%</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Vigentes</div>
              <div className="mt-1 text-xl font-bold text-slate-900 tabular-nums">{formatNum(data.vigentes)}</div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Asistidos</div>
              <div className="mt-1 text-xl font-bold text-emerald-600 tabular-nums">{formatNum(data.asistidos)}</div>
            </div>
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">No-Show</div>
              <div className="mt-1 text-xl font-bold text-amber-700 tabular-nums">{formatNum(data.no_show)}</div>
              <div className="text-xs text-amber-600 font-semibold tabular-nums">{data.pct_no_show}%</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">% Confirmación</div>
              <div className="mt-1 text-xl font-bold text-blue-600 tabular-nums">{data.pct_confirmacion}%</div>
            </div>
          </div>

          {/* Cálculo explicado */}
          <SectionCard title="¿Cómo se calcula?" subtitle="Misma fórmula que Grafana — Equipo de Acceso">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">1</div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Total reservas (consultas presenciales)</div>
                  <div className="text-sm text-slate-500 tabular-nums">{formatNum(data.total_reservas)} citas con AREA = 'Consultas'</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-rose-100 flex items-center justify-center text-xs font-bold text-rose-600 shrink-0">2</div>
                <div>
                  <div className="text-sm font-medium text-slate-900">Vigentes = Total − Anulados</div>
                  <div className="text-sm text-slate-500 tabular-nums">{formatNum(data.total_reservas)} − {formatNum(data.anulados)} = {formatNum(data.vigentes)}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-600 shrink-0">3</div>
                <div>
                  <div className="text-sm font-medium text-slate-900">No-Show = Vigentes − Asistidos</div>
                  <div className="text-sm text-slate-500 tabular-nums">{formatNum(data.vigentes)} − {formatNum(data.asistidos)} = <strong className="text-amber-600">{formatNum(data.no_show)}</strong></div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-600 shrink-0">4</div>
                <div>
                  <div className="text-sm font-medium text-slate-900">%No-Show = No-Show / Vigentes</div>
                  <div className="text-sm text-slate-500 tabular-nums">{formatNum(data.no_show)} ÷ {formatNum(data.vigentes)} = <strong className="text-amber-600">{data.pct_no_show}%</strong></div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* No-Show por centro */}
          {hasCentros && (
            <SectionCard title="No-Show por centro" subtitle="% sobre citas vigentes por centro">
              <div className="space-y-1">
                {data.por_centro.map((c) => (
                  <div key={c.centro} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-sm text-slate-700">{c.centro}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500 tabular-nums">{formatNum(c.no_show)} / {formatNum(c.vigentes)}</span>
                      <span className={`text-sm font-bold tabular-nums ${c.pct > 18 ? "text-rose-600" : c.pct > 15 ? "text-amber-600" : "text-emerald-600"}`}>
                        {c.pct}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <BarChart
                  categories={data.por_centro.map((c) => c.centro)}
                  series={[{ name: "% No-Show", data: data.por_centro.map((c) => c.pct) }]}
                  colors={["#F59E0B"]}
                  height={220}
                  horizontal
                />
              </div>
            </SectionCard>
          )}

          {/* Impacto operacional */}
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div className="text-xs font-semibold text-amber-700 uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Impacto operacional
            </div>
            <p className="text-sm text-slate-700">
              {formatNum(data.no_show)} citas vigentes donde el paciente no se presentó.
              Esto representa el <strong>{data.pct_no_show}%</strong> de las {formatNum(data.vigentes)} citas vigentes.
            </p>
            <p className="text-sm text-slate-700">
              Si cada slot tiene un valor promedio de $35.000 CLP, el impacto estimado es de
              <strong className="tabular-nums"> ${formatNum(Math.round(data.no_show * 35 / 1000))} MM CLP</strong> en el período.
            </p>
          </div>

          {/* Contexto técnico */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
            <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider flex items-center gap-2">
              <Database className="w-3.5 h-3.5" /> Metodología
            </div>
            <p className="text-sm text-slate-700">
              <strong>Fuente:</strong> {data.fuente}
            </p>
            <p className="text-sm text-slate-700">
              <strong>Alineado con:</strong> Dashboard Grafana del Equipo de Acceso. Misma tabla, misma fórmula, mismos filtros.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Filtro:</strong> Solo consultas presenciales (<code className="text-xs bg-slate-100 px-1 rounded">AREA = 'Consultas'</code>).
              No incluye procedimientos, radiología, kinesiología, telemedicina ni laboratorio.
            </p>
            <p className="text-sm text-slate-700">
              <strong>Definición:</strong> Vigentes = Total − Anulados. No-Show = Vigentes − Asistidos.
              %NS = No-Show / Vigentes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
